<template>
  <v-container fluid v-if="initialized">
    <div class="d-flex justify-space-between align-center">
      <h2>{{ $t('root_cause_analysis') }}</h2>
      <v-btn
        v-if="userLoaded && user.can('view_rootentity')"
        outlined
        color="primary"
        :to="'/customer/' + customerContext.id + '#assessment-terms'">
        <v-icon class="mr-1 ml-n2">mdi-clipboard-text</v-icon>
        {{ $t('edit_assessment_terms' )}}
      </v-btn>
    </div>
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col xl="3" lg="3" md="6" cols="12">
          <v-text-field
            :label="$t('route_name')"
            v-model="searchCriteria.route_name"
            @keyup.enter.native="initiateSearch()"
            prepend-inner-icon="mdi-road-variant">
          </v-text-field>
        </v-col>
        <v-col class="d-flex align-center" xl="3" lg="3" md="6" cols="12">
          <date-picker
            v-model="searchCriteria.startedAfter"
            :timezone="customerTimezone"
            :label="$t('start_date')"
            prepend-inner-icon="mdi-calendar"
            reset-on-timezone-change />
          <date-picker
            v-model="searchCriteria.startedBefore"
            :timezone="customerTimezone"
            prepend-inner-icon="mdi-calendar"
            :label="$t('end_date')"
            reset-on-timezone-change
            end-of-day />
        </v-col>
        <v-col xl="3" lg="3" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('vehicle', 2)"
            v-model="searchCriteria.vehicles"
            :items="vehicleOptions"
            prepend-inner-icon="mdi-truck"/>
        </v-col>
        <v-col xl="3" lg="3" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('violation', 2)"
            v-model="searchCriteria.violation_type"
            :items="violationOptions"
            prepend-inner-icon="mdi-alert"/>
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('pending_assessment', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('download') }}
        </v-btn>
        <vf-tooltip :text="$t('download')">
          <v-btn class="hidden-lg-and-up" fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>
    <v-alert type="success" prominent v-if="showNoAssessmentsMessage">
      <h6>{{ $t('good_job') }}</h6>
      <p>{{ $t('all_root_causes_have_been_completed') }}</p>
    </v-alert>
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table clickable-rows mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @click:row="openAssessment"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">

        <template v-slot:item.name="row">
          <router-link :to="assessmentLink(row.item)" @click.native.stop>
            <v-icon small color="primary" class="mr-1">mdi-road-variant</v-icon>
            {{ row.item.name }}
          </router-link>
        </template>

        <template v-slot:item.vehicle="row">
          <router-link
            v-if="row.item.vehicle != null"
            :to="`/vehicle/${row.item.vehicle.id}`"
            @click.native.stop>
            <v-icon small color="primary" class="mr-1">mdi-truck</v-icon>{{ row.item.vehicle.name }}
          </router-link>
          <a v-else
            class="font-italic"
            target="_blank"
            @click.stop
            :href="`https://${zendeskDomain}/hc/en-us/articles/360042724133-Vehicle-Cannot-Be-Displayed`">
            {{ $t('cannot_be_displayed') }}
          </a>
        </template>

        <template v-slot:item.start="row">
          <span class="text-no-wrap">
            <multi-time-display v-if="row.item.start" :time="row.item.start"/>
            <span v-else class="not-set">{{ $t('not_set') }}</span>
          </span>
        </template>

        <template v-slot:item.end="row">
          <span class="text-no-wrap">
            <multi-time-display v-if="row.item.end" :time="row.item.end"/>
            <span v-else class="not-set">{{ $t('not_set') }}</span>
          </span>
        </template>

        <template v-slot:item.cooler_status="row">
          <span v-for="(result, index) in formatViolations(row.item.route_violations, 'cooler')" :key="index">
            <vf-tooltip :text="result.tooltip">
              <v-icon :color="result.color">{{ result.icon }}</v-icon>
            </vf-tooltip>
          </span>
        </template>

        <template v-slot:item.freezer_status="row">
          <span v-for="(result, index) in formatViolations(row.item.route_violations, 'freezer')" :key="index">
            <vf-tooltip :text="result.tooltip">
              <v-icon :color="result.color">{{ result.icon }}</v-icon>
            </vf-tooltip>
          </span>
        </template>

        <template v-slot:no-data>
          <span v-if="loadFailed" class="danger--text">{{ $t('network_error_message') }}</span>
          <span v-else>{{ $t('no_results') }}</span>
        </template>

        <template v-slot:item.assess="row">
          <vf-tooltip :text="$tc('route_assessment')">
            <router-link :to="assessmentLink(row.item)" @click.native.stop>
              <v-icon color="primary">mdi-clipboard-edit-outline</v-icon>
            </router-link>
          </vf-tooltip>
        </template>
      </v-data-table>
    </div>
  </v-container>
  <v-container v-else>
    <index-table-skeleton>
      <template v-slot:header>
        <h2>{{ $t('root_cause_analysis') }}</h2>
      </template>
    </index-table-skeleton>
  </v-container>
</template>

<script>
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import DatePicker from '@/components/shared/DatePicker'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import VfTooltip from '@/components/shared/VfTooltip'
import IndexTableSkeleton from '@/components/shared/IndexTableSkeleton'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import RouteViewTabs from '@/components/route/RouteViewTabs'
import Moment from 'moment-timezone'
import { isEqual, map, sortBy, each, filter, uniqBy } from 'lodash'

export default {
  name: 'RootCauseAnalysis',
  components: {
    TablePaginationControls,
    DatePicker,
    MultiTimeDisplay,
    VfAutocomplete,
    VfTooltip,
    IndexTableSkeleton
  },
  mixins: [CurrentUserMixin, RemoteDataTableMixin, DateTimeUtilsMixin, BrowserUtilitiesMixin, ApiUtilitiesMixin],
  data () {
    return {
      model: 'Route',
      searchCriteria: this.getDefaultSearchCriteria(),
      vehicles: [],
      // Whether the vehicle filter options have finished loading. The table itself doesn't depend on them, but we
      // hold the skeleton until they're in so the filter bar doesn't pop in underneath the user.
      vehiclesLoaded: false,
      initialControlsMatchDefaults: false,
      firstSearch: null,
      // Whether the most recent search failed. See loadRemoteData() below.
      loadFailed: false,
      remoteData: {
        rowsPerPage: 10,
        // Most recent routes first: those are the assessments people actually action. Older pending assessments run
        // to five figures on long-lived customers and are reachable through the date filters.
        sortBy: 'start',
        sortDesc: true
      },
      tableFields: [
        {
          value: 'name',
          text: this.$tc('route'),
          sortable: true
        },
        {
          value: 'vehicle',
          text: this.$tc('vehicle'),
          sortable: false
        },
        {
          value: 'start',
          text: this.$t('route_start'),
          sortable: true
        },
        {
          value: 'end',
          text: this.$t('route_end'),
          sortable: true
        },
        {
          value: 'cooler_status',
          text: this.$t('cooler'),
          sortable: false
        },
        {
          value: 'freezer_status',
          text: this.$t('freezer'),
          sortable: false
        },
        {
          value: 'assess',
          text: '',
          sortable: false
        }
      ]
    }
  },
  methods: {
    /**
     * The route view, opened directly on its Root Cause tab. Assessments are completed there rather than inline
     * (VF-4301): rendering an assessment form per row required the interpolated sensor data for every route on the
     * page, which timed out for customers with a large backlog.
     *
     * @param {RouteRecord} route
     * @return string
     */
    assessmentLink (route) {
      return `/route/${route.id}#${RouteViewTabs.ROOT_CAUSE}`
    },
    /**
     * Row click handler - go and assess the clicked route.
     *
     * @param {RouteRecord} route
     * @return undefined
     */
    openAssessment (route) {
      this.$router.push(this.assessmentLink(route))
    },
    /**
     * The shared mixin marks the table loaded in a `finally`, so a request that errored is indistinguishable from a
     * genuinely empty result: zero rows, isLoaded true. This page then showed "All root causes have been completed"
     * over a search that had actually failed, while the side nav badge still showed a pending count (VF-4301).
     * Record the failure so the banner and the empty-table text can tell the two apart.
     *
     * @return {Promise<void>}
     */
    async loadRemoteData () {
      this.loadFailed = false
      try {
        await RemoteDataTableMixin.methods.loadRemoteData.call(this)
      } catch (error) {
        this.loadFailed = true
        throw error
      }
    },
    /**
     * Fetch the vehicles used in the vehicle options filter
     */
    async fetchVehicles () {
      try {
        const response = await this.$orm('Vehicle')
          .findAll({ customer_id: this.customerContext.id, sort: 'name', 'per-page': 0 })
        this.vehicles = response.getItems()
      } catch (error) {
        this.handleError(error)
      } finally {
        this.vehiclesLoaded = true
      }
    },
    /**
     * Returns the default search criteria
     * @return Object
     */
    getDefaultSearchCriteria () {
      return {
        route_name: null,
        vehicles: [],
        startedAfter: Moment().subtract(30, 'days').startOf('day').format(),
        startedBefore: Moment().endOf('day').format(),
        violation_type: []
      }
    },
    /**
     * Reset the table to the default search parameters
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.refreshResults()
    },
    /**
     * Reset the paging and sorting properties, keeping this page's non-default sort order.
     *
     * @return undefined
     */
    resetRemoteDataTableNavigation () {
      this.remoteData.currentPage = 1
      this.remoteData.sortBy = 'start'
      this.remoteData.sortDesc = true
    },
    /**
     * Constructs an object of key value pairs that represent the current search criteria.
     * @return object
     */
    buildSearchCriteria () {
      const fields = [
        'id',
        'name',
        'start',
        'end',
        'vehicle.id',
        'vehicle.name',
        'route_violations.zone',
        'route_violations.type',
        'route_violations.level'
      ]

      const expand = [
        'vehicle',
        'route_violations'
      ]

      const searchCriteria = {
        expand: expand.join(','),
        fields: fields.join(','),
        customer_id: this.customerContext.id,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage,
        unassessed_violations: true
      }

      if (this.searchCriteria.route_name != null) {
        searchCriteria.name = this.searchCriteria.route_name
      }

      if (this.searchCriteria.vehicles.length > 0) {
        searchCriteria.vehicle_id = this.searchCriteria.vehicles.join(',')
      }

      if (this.searchCriteria.startedAfter != null) {
        searchCriteria.start_after = this.searchCriteria.startedAfter
      } else {
        // Default to routes/violations within the last 30 days if no date is
        // provided
        searchCriteria.start_after = Moment.tz(Moment().subtract(30, 'days'), this.customerTimezone || 'UTC').startOf('day').format()
      }

      if (this.searchCriteria.startedBefore != null) {
        searchCriteria.start_before = this.searchCriteria.startedBefore
      }

      if (this.searchCriteria.violation_type.length > 0) {
        searchCriteria.violation_type = this.searchCriteria.violation_type.join(',')
      }

      if (this.remoteData.sortBy != null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }

      return searchCriteria
    },
    /**
     * Update options and reload the table when customer context changes
     */
    customerContextChangeHandler (newCustomerContext, oldCustomerContext) {
      this.fetchVehicles()
      this.resetTable()
    },
    /**
     * Depending on the violation type and zone, create presentation data for the violation icons.
     * Only fatal violations are shown: they are what makes a route appear in this list in the first place.
     *
     * @param violations
     * @param zone
     * @returns {Array}
     */
    formatViolations (violations, zone) {
      const output = []
      violations = filter(violations, (violation) => {
        return violation.zone === zone && violation.level === RouteViolationRecord.LEVEL_FATAL
      })
      each(violations, (violation) => {
        if (violation.type === RouteViolationRecord.TYPE_NODATA) {
          output.push({ icon: 'mdi-cancel', color: 'danger', tooltip: this.$t('fatal_violation:no-data') })
        }
        if (violation.type === RouteViolationRecord.TYPE_HOT) {
          output.push({ icon: 'mdi-fire', color: 'danger', tooltip: this.$t('fatal_violation:hot') })
        }
        if (violation.type === RouteViolationRecord.TYPE_COLD) {
          output.push({ icon: 'mdi-snowflake', color: 'primary', tooltip: this.$t('fatal_violation:cold') })
        }
        if (violation.type === RouteViolationRecord.TYPE_GAP) {
          output.push({ icon: 'mdi-clock', color: 'warning', tooltip: this.$t('fatal_violation:time-gap') })
        }
      })
      if (output.length === 0) {
        output.push({ icon: 'mdi-check', color: 'success', tooltip: this.$t('no_violations') })
      }
      return uniqBy(output, x => x.icon)
    },
    /**
     * Override mixin method to add a parameter
     * @param firstSearch
     */
    initiateSearch (firstSearch = false) {
      this.firstSearch = firstSearch
      this.remoteData.currentPage = 1
      if (this.model != null) {
        this.$gtag.event('search', { event_category: this.model })
      }
      this.refreshResults()
    }
  },
  computed: {
    zendeskDomain () {
      return process.env.VUE_APP_ZENDESK_DOMAIN
    },
    /**
     * Returns a flag that indicates if the search criteria matches the default values
     *
     * @return Boolean
     */
    tableControlsMatchDefaults () {
      return isEqual(this.searchCriteria, this.getDefaultSearchCriteria()) &&
        this.remoteData.currentPage === 1
    },
    /**
     * Returns whether we should display the "Good Job" message (when there are no assessments to be done)
     * The logic for this is as follows:
     * 1) Must be the initial search on page load
     * 2) The initial search should be the default, and we should have come into the page with the default
     * 3) The search has finished and has returned zero rows.
     */
    showNoAssessmentsMessage () {
      return this.firstSearch &&
        !this.loadFailed &&
        this.tableControlsMatchDefaults &&
        this.initialControlsMatchDefaults &&
        this.remoteData.isLoaded &&
        this.remoteData.totalRows === 0
    },
    /**
     * Options property for violations picker
     * @returns Object
     */
    violationOptions () {
      return [
        { text: this.$t('fatal_violation:cold'), value: RouteViolationRecord.TYPE_COLD },
        { text: this.$t('fatal_violation:hot'), value: RouteViolationRecord.TYPE_HOT },
        { text: this.$t('fatal_violation:no-data'), value: RouteViolationRecord.TYPE_NODATA },
        { text: this.$t('fatal_violation:time-gap'), value: RouteViolationRecord.TYPE_GAP }
      ]
    },
    /**
     * Options property for vehicle picker
     * @returns [Object]
     */
    vehicleOptions () {
      const vehicleOptions = map(this.vehicles, (vehicle) => {
        return { text: vehicle.name, value: vehicle.id }
      })
      return sortBy(vehicleOptions, v => v.text)
    },
    /**
     * Whether the filter options have been loaded yet
     * @returns boolean
     */
    initialized () {
      return this.vehiclesLoaded
    }
  },
  mounted () {
    this.initialControlsMatchDefaults = this.tableControlsMatchDefaults
    this.updateBrowserTitle(this.$t('root_cause_analysis'))
    this.fetchVehicles()
    this.initiateSearch(true)
  }
}
</script>

<style lang="scss" scoped>
// need a little bit less padding in the table so we can cram more stuff in a reasonable sized screen. The dense prop
// doesn't seem to affect the horizontal padding.
.vf-table ::v-deep(th),
.vf-table ::v-deep(td) {
  padding: 0 8px !important;
}
.clickable-rows ::v-deep(tbody tr) {
  cursor: pointer;
}
.not-set {
  color: #9e9e9e;
  font-style: italic;
  white-space: nowrap;
}
</style>
